/**
 * Home screen — the customer landing page. Static labels are plain strings;
 * anything parameterized or pluralized is a function (its signature is the
 * contract every locale must satisfy). Relative times, dates and headline
 * numbers are NOT here — they are produced by the locale-aware Intl helpers in
 * `i18n/format.ts` (`formatAgo`, `formatDate`, `formatNumber`) and by the
 * date helpers in `components/home/home-format.ts`.
 */

export const home = {
  // Hero greeting + shell
  greeting: (hour: number): string => (hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'),
  greetingFallbackName: 'there',
  yourTeam: 'your team',
  loadingWorkshop: 'Loading your workshop…',

  // Lede — the month's story in one sentence
  ledeDefault: 'Here is what your flows have been up to.',
  ledeEmpty: 'Nothing has run yet this month. Start a flow and your results will land here.',
  ledeFirstRuns: (waiting: number, live: number) =>
    `Your first runs of the month are underway${
      waiting === 1
        ? ' — one run is waiting on you right now.'
        : waiting > 1
          ? ` — ${waiting} runs are waiting on you right now.`
          : live === 1
            ? ' — one run is in motion right now.'
            : live > 1
              ? ` — ${live} runs are in motion right now.`
              : '.'
    }`,
  ledeDeliveredPrefix: 'Your flows have delivered',
  ledeDeliveredMid: 'across',
  ledeDeliveredSuffix: 'this month',
  ledeTail: (waiting: number, live: number) =>
    waiting === 1
      ? ' — and one run is waiting on you right now.'
      : waiting > 1
        ? ` — and ${waiting} runs are waiting on you right now.`
        : live === 1
          ? ' — and one run is in motion right now.'
          : live > 1
            ? ` — and ${live} runs are in motion right now.`
            : '.',
  docsCount: (n: number) => (n === 1 ? '1 document' : `${n} documents`),
  runsCount: (n: number) => (n === 1 ? '1 run' : `${n} runs`),

  // Hero cards
  allQuietTitle: 'All quiet — nothing needs you right now',
  allQuietSub: 'Start a flow and watch the workshop get to work.',
  browseFlows: 'Browse flows',
  hasQuestionForYou: (name: string) => `${name} has a question for you`,
  attnWithQuestion: (question: string, ago: string) => `“${question}” · asked ${ago}`,
  attnWaitingReply: (ago: string) => `Waiting on your reply · asked ${ago}`,
  moreWaiting: (extra: number) => `+${extra} more waiting`,
  answer: 'Answer',
  liveBadge: 'live',
  startedAgo: (ago: string) => `started ${ago}`,
  byPerson: (name: string) => `by ${name}`,
  moreRunning: (extra: number) => `+${extra} more running`,
  watch: 'Watch',

  // Workshop stage captions
  stageSetting: 'Setting the stage…',
  stageBusyAndWaiting: (live: number, waiting: number) =>
    `${live} run${live === 1 ? '' : 's'} in motion · ${waiting} waiting on you`,
  stageBusy: (live: number) => `The workshop is busy — ${live} run${live === 1 ? '' : 's'} in motion`,
  stagePaused: 'The workshop is paused — a question is waiting on you',
  stageQuiet: 'The workshop is quiet — ready when you are',

  // Month stats band
  thisMonthAt: (tenant: string) => `This month at ${tenant}`,
  monthRange: (monthName: string, day: number) => `${monthName} 1 – ${day}`,
  partialChip: 'partial',
  partialTooltip: 'Very busy month — the numbers below are lower bounds.',
  fullHistory: 'Full history',
  statRunsCompleted: 'Runs completed',
  statSuccessRate: 'Success rate',
  statDocsDelivered: 'Documents delivered',
  statMedianTurnaround: 'Median turnaround',
  runsOfTotal: (succeeded: number, total: number) => `${succeeded} of ${total} run${total === 1 ? '' : 's'}`,
  noFinishedRuns: 'no finished runs yet',
  docsThisWeek: (n: number) => `▲ ${n} this week`,
  noneThisWeek: 'none this week',
  fromLaunchToFiles: 'from launch to files',
  runsPerDay14: 'Runs per day · last 14 days',
  dayRuns: (day: string, count: number) => `${day} · ${count} run${count === 1 ? '' : 's'}`,
  firstActiveMonth: 'first active month',
  paceUp: (pct: number, month: string) => `▲ ${pct}% vs ${month}`,
  paceDown: (pct: number, month: string) => `▼ ${pct}% vs ${month}`,
  paceEven: (month: string) => `on pace with ${month}`,

  // Latest results
  latestResults: 'Latest results',
  history: 'History',
  firstDeliverable: 'Your first deliverable will land here once a run finishes.',
  outcomeDone: 'Done',
  outcomePartial: 'Partial',
  outcomeFailed: 'Failed',
  startedByName: (name: string) => `Started by ${name}`,
  filesCount: (n: number) => (n === 1 ? '1 file' : `${n} files`),

  // Quick flows
  startSomething: 'Start something',
  allFlows: 'All flows',
  noFlowsAvailable: 'No flows are available for your account yet. Check back soon.'
}
